import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BiPencil, BiTrash } from 'react-icons/bi';
import { PiDotsThree } from 'react-icons/pi';
import { z } from 'zod';

import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import AddButton from '@/components/Button/AddButton';
import { Input, Select, TextArea } from '@/components/Inputs';
import { LoaderSpinner } from '@/components/LoaderSpinner/LoaderSpinner';
import { Popup, PopupSection, type PopupSectionItem } from '@/components/Popup/Popup';
import { Tile } from '@/components/Tile/Tile';
import { useClickOutside } from '@/hooks/useClickOutside';
import { toDegreesMinutes } from '@/utils/position';

import { currentLocalTime, timeMaskProps } from '../../logbookTime';
import { useEntryFix } from '../../useEntryFix';
import { GARBAGE_CATEGORIES, type TrashEntry } from '../types';

import s from './TrashEntriesSection.module.scss';

const CATEGORY_OPTIONS = GARBAGE_CATEGORIES.map(({ code, label }) => ({
  value: code,
  label: `${code}. ${label}`,
}));

const categoryLabel = (code: string): string => {
  const found = GARBAGE_CATEGORIES.find((c) => c.code === code);
  return found ? `${found.code}. ${found.label}` : '';
};

const disposalMethods = (entry: TrashEntry): string =>
  [
    entry.toSea && 'To Sea',
    entry.toReceptionFacility && 'To Reception Facility',
    entry.incineration && 'Incineration',
  ]
    .filter(Boolean)
    .join(', ');

interface TrashEntriesSectionProps {
  entries: TrashEntry[];
  editingId: string | null;
  addingNew: boolean;
  onStartAdd(): void;
  onStartEdit(id: string): void;
  onCancel(): void;
  onSaveEdit(id: string, patch: Partial<TrashEntry>): void;
  onAddNew(values: Omit<TrashEntry, 'id'>): void;
  onRemove(id: string): void;
}

export function TrashEntriesSection({
  entries,
  editingId,
  addingNew,
  onStartAdd,
  onStartEdit,
  onCancel,
  onSaveEdit,
  onAddNew,
  onRemove,
}: TrashEntriesSectionProps) {
  return (
    <Tile>
      <section className={s.section}>
        <h6 className={s.sectionTitle}>Disposal Entries</h6>

        {entries.length === 0 && !addingNew && (
          <p className={s.empty}>Please add an entry to get started.</p>
        )}

        {entries.map((entry, index) =>
          entry.id === editingId ? (
            <TrashEntryEdit
              key={entry.id}
              number={index + 1}
              entry={entry}
              onCancel={onCancel}
              onSubmit={(values) => onSaveEdit(entry.id, values)}
            />
          ) : (
            <TrashEntryItem
              key={entry.id}
              number={index + 1}
              entry={entry}
              onEdit={() => onStartEdit(entry.id)}
              onDelete={() => onRemove(entry.id)}
            />
          ),
        )}

        {addingNew ? (
          <TrashEntryEdit
            number={entries.length + 1}
            onCancel={onCancel}
            onSubmit={(values) => onAddNew(values)}
          />
        ) : (
          <AddButton label="Disposal Entry" onClick={onStartAdd} />
        )}
      </section>
    </Tile>
  );
}

interface TrashEntryItemProps {
  number: number;
  entry: TrashEntry;
  onEdit(): void;
  onDelete(): void;
}

function TrashEntryItem({ number, entry, onEdit, onDelete }: TrashEntryItemProps) {
  const [opened, setOpened] = useState(false);
  const ref = useClickOutside<HTMLButtonElement>(() => setOpened(false));

  const actionsList: PopupSectionItem[] = [
    { label: 'Edit', onClick: onEdit, icon: <BiPencil /> },
    { label: 'Delete', onClick: onDelete, icon: <BiTrash /> },
  ];

  return (
    <article className={s.inputsGroup}>
      <div className={s.groupTitle}>
        <p>Entry #{number}</p>
        <button type="button" className={s.actions} onClick={() => setOpened((o) => !o)} ref={ref}>
          <PiDotsThree size={20} />
          {opened && (
            <Popup>
              <PopupSection items={actionsList} title="" onItemClick={() => setOpened(false)} />
            </Popup>
          )}
        </button>
      </div>
      <section className={s.inputs}>
        <Field label="Time" value={entry.dateTime} />
        <Field label="Category" value={categoryLabel(entry.category)} />
        <Field label="Estimated Amount" value={entry.estimatedAmount} />
        <Field label="Certification / Signature" value={entry.certificationSignature} />
        <Field label="Position / Remarks" value={entry.positionRemarks} />
        <Field label="Disposal Method" value={disposalMethods(entry)} />
      </section>
    </article>
  );
}

interface TrashEntryEditProps {
  number: number;
  entry?: TrashEntry;
  onCancel(): void;
  onSubmit(values: Omit<TrashEntry, 'id'>): void;
}

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const FIELD_MAX = 100;
const REMARKS_MAX = 500;

const entrySchema = z.object({
  dateTime: z.string().refine((v) => !v || TIME_RE.test(v), 'Use HH:MM format (00:00–23:59)'),
  category: z.string().min(1, 'Please select a category.'),
  estimatedAmount: z.string().max(FIELD_MAX, `Max ${FIELD_MAX} characters`),
  certificationSignature: z.string().max(FIELD_MAX, `Max ${FIELD_MAX} characters`),
  positionRemarks: z.string().max(REMARKS_MAX, `Max ${REMARKS_MAX} characters`),
  toSea: z.boolean(),
  toReceptionFacility: z.boolean(),
  incineration: z.boolean(),
});

type EntryFormValues = z.infer<typeof entrySchema>;

function TrashEntryEdit({ number, entry, onCancel, onSubmit }: TrashEntryEditProps) {
  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    mode: 'onChange',
    defaultValues: {
      dateTime: entry?.dateTime ?? currentLocalTime(),
      category: entry?.category ?? '',
      estimatedAmount: entry?.estimatedAmount ?? '',
      certificationSignature: entry?.certificationSignature ?? '',
      positionRemarks: entry?.positionRemarks ?? '',
      toSea: entry?.toSea ?? false,
      toReceptionFacility: entry?.toReceptionFacility ?? false,
      incineration: entry?.incineration ?? false,
    },
  });
  // A new entry starts at the ship's position, which the record book asks for as lat/long.
  const locating = useEntryFix(!entry, { getValues, setValue }, (coords) => ({
    positionRemarks: toDegreesMinutes(coords.latitude, coords.longitude),
  }));

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={s.inputsGroup}>
      <div className={s.groupTitle}>
        <p>Entry #{number}</p>
      </div>
      <section className={s.editInputs}>
        <Input
          label="Time"
          placeholder="HH:MM"
          error={errors.dateTime?.message}
          {...timeMaskProps(register('dateTime'))}
        />
        <Controller
          name="category"
          control={control}
          render={({ field }) => (
            <Select
              label="Category"
              placeholder="Select category"
              options={CATEGORY_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              error={errors.category?.message}
            />
          )}
        />
        <Input
          label="Estimated Amount Discharged or Incinerated"
          placeholder="e.g. 0.5 m³"
          error={errors.estimatedAmount?.message}
          {...register('estimatedAmount')}
        />
        <Input
          label="Certification / Signature"
          error={errors.certificationSignature?.message}
          {...register('certificationSignature')}
        />
        <TextArea
          label="Position of the Ship / Remarks (incl. accidental loss)"
          placeholder={locating ? 'Locating…' : '43°44.123′N, 7°25.476′E, no loss'}
          suffixNode={locating && <LoaderSpinner size={16} fullHeight={false} label="" />}
          error={errors.positionRemarks?.message}
          className={s.stretch}
          {...register('positionRemarks')}
        />
        <div className={s.checkboxRow}>
          <label className={s.checkbox}>
            <input type="checkbox" {...register('toSea')} />
            <span>To Sea</span>
          </label>
          <label className={s.checkbox}>
            <input type="checkbox" {...register('toReceptionFacility')} />
            <span>To Reception Facility</span>
          </label>
          <label className={s.checkbox}>
            <input type="checkbox" {...register('incineration')} />
            <span>Incineration</span>
          </label>
        </div>
      </section>
      <div className={s.groupItemActions}>
        <ButtonOutlinedSmall type="button" onClick={onCancel}>
          Cancel
        </ButtonOutlinedSmall>
        <ButtonSmall type="submit">{entry ? 'Save' : 'Add'}</ButtonSmall>
      </div>
    </form>
  );
}

interface FieldProps {
  label: string;
  value?: string;
  stretch?: boolean;
}

function Field({ label, value, stretch }: FieldProps) {
  return (
    <article className={clsx(s.field, stretch && s.stretch)}>
      <span className={`body-medium ${s.fieldLabel}`}>{label}</span>
      <div className={s.fieldValue}>{value && value.length ? value : '-'}</div>
    </article>
  );
}
